import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import CommonImage from '@components/CommonImage';
import DesignItem from '@components/DesignItem';
import NoData from '@components/NoData';
import TimeBasedLoader from '@components/TimeBasedLoader';
import { toastError, toastWarning } from '@components/Toast';
import AddDesignToCollection from '@components/design-uploads/AddDesignToCollection';
import BottomPopup from '@components/modals/BottomPopup';

import { useCommonTags } from '@hooks/useCommonTags';

import CollectionPresentation from '@features/collection/components/CollectionPresentation';
import TagUpdateModal from '@features/collection/details/TagUpdateModal';
import FavouriteProductsListView from '@features/favourites/components/FavouriteProductsListView';
import InventoryAndStickerModal from '@features/favourites/components/InventoryAndStickerModal';
import SampleLocationDetailsModal from '@features/favourites/components/SampleLocationDetailsModal';
import SelectFolderPopup from '@features/favourites/components/SelectFolderPopup';
import UpdateSampleLocation from '@features/favourites/components/UpdateSampleLocation';
import { groupBy } from '@features/favourites/utils/favouriteUtil';
import CreateOrderModal from '@features/order/components/CreateOrderModal';

import { clearDesignViewModalInfo, setDesignViewModalInfo } from '@store/actions/DesignViewModalAction';
import { closeLoader, openLoader } from '@store/actions/LoaderAction';
import { likeProduct } from '@store/design/action';
import { fetchFavouriteProducts } from '@store/favourite/actions';
import { storeProductData } from '@store/actions/DesignAction';

import { createSetOrder, fetchAllProductTags } from '@services/collectionService';
import { addToDraft } from '@services/favouriteService';

import ApiClient from '@lib/apiClient';

import { downloadBlobFIle, isPageReachBottom, printApiError } from '@utils/Util';

import { LIST_VIEW_TYPE } from '@/constants/constant';

import CreateSetOrder from '@/features/collection/details/CreateSetOrder';
import DeleteSetOrder from '@/features/collection/details/DeleteSetOrder';

const ProductFavourite = ({
	setIsLoading,
	designList,
	setDesignList,
	selectedProducts,
	setSelectedProducts,
	defaultViewType,
	allFavourites,
	selectedSortingOption,
	page,
	folderId,
	setPage,
	folderInfo,
	fetchDraftedProducts,
	getCategoryIdList,
	brandId,
	createFilterParams
}) => {
	const [designIdList, setDesignIdList] = useState([]);
	const [designIdToView, setDesignIdToView] = useState(null);
	const [startLoader] = useState(false);
	const [isShowAddCollectionPopup, setIsShowAddCollectionPopup] = useState(false);
	const dispatch = useDispatch();
	const scrollRef = useRef({});
	let groupedData = groupBy(designList?.data || [], selectedSortingOption?.code);
	const [isShowInventoryModal, setIsShowInventoryModal] = useState(false);
	const [isUnSelectAll, setIsUnSelectAll] = useState(false);
	const [isShowLocationModal, setIsShowLocationModal] = useState(false);
	const [selectedLocationsToDownload, setSelectedLocationsToDownload] = useState([]);
	const [isShowUpdateLocationModal, setIsShowUpdateLocationModal] = useState(false);
	const [isShowSelectFolderPopup, setIsShowSelectFolderPopup] = useState(false);
	const [isCreateNewFolder, setIsCreateNewFolder] = useState(false);
	const [isPlayPresentation, setIsPlayPresentation] = useState(false);
	const [isSelectOnNextPage, setIsSelectOnNextPage] = useState(false);
	const pageRef = useRef(page);
	const [isShowTags, setIsShowTags] = useState(false);
	const [productWiseTags, setProductWiseTags] = useState([]);
	const [isOpenPlaceOrder, setIsOpenPlaceOrder] = useState(false);
	const [productIdsWithoutBodyFabric, setProductIdsWithoutBodyFabric] = useState([]);
	const [isShowSetModal, setIsShowSetModal] = useState(false);
	const [selectedSetProduct, setSelectedSetProduct] = useState(null);
	const [selectedProductsWithDetails, setSelectedProductsWithDetails] = useState([]);
	const [isOpenDeleteConfirmation, setIsOpenDeleteConfirmation] = useState(false);
	const [setOrderType, setSetOrderType] = useState('');
	const [requestType, setRequestType] = useState('CREATE');

	const commonTags = useCommonTags(
		designList?.data.map((item) => item.productResponse) || [],
		selectedProducts
	);

	const fetchFavProducts = () => {
		dispatch(
			fetchFavouriteProducts(
				0,
				20,
				'',
				selectedSortingOption?.code,
				folderId,
				false,
				createFilterParams(),
				defaultViewType === LIST_VIEW_TYPE.LIST_VIEW
			)
		);
	};

	const fetchAllTags = async (search) => {
		try {
			dispatch(openLoader());
			let params = '?type=PRODUCT_COLLECTION_TAGS';
			if (search) {
				params += `&search=${search}`;
			}
			const data = await fetchAllProductTags(params);

			setProductWiseTags(data);
			dispatch(closeLoader());
		} catch (error) {
			dispatch(closeLoader());
			printApiError(error);
		}
	};

	const setCurrentState = (data) => {
		scrollRef.current = data;
	};

	const excludeLinkedProducts = () => {
		const filteredData = allFavourites?.data
			?.filter((product) => product?.isLinked)
			.map((product) => product?.productResponse?.id);
		return filteredData;
	};

	useEffect(() => {
		setDesignList(allFavourites);
		setCurrentState(allFavourites);
		let designIds = allFavourites?.data?.map((product) => product.id);
		setDesignIdList(designIds);
		pageRef.current = allFavourites?.totalPages;
	}, [allFavourites]);

	useEffect(() => {
		setCurrentState(allFavourites);
	}, [allFavourites]);

	useEffect(() => {
		if (page !== 0) {
			dispatch(
				fetchFavouriteProducts(
					page,
					30,
					'',
					selectedSortingOption?.code,
					folderId,
					false,
					getCategoryIdList(),
					defaultViewType === LIST_VIEW_TYPE.LIST_VIEW
				)
			);
		}
	}, [page]);

	useEffect(() => {
		if (isSelectOnNextPage) {
			handleSelectAll();
		}
	}, [isSelectOnNextPage, designList?.currentPage]);

	useEffect(() => {
		document.addEventListener('scroll', handleScroll);
		return () => {
			document.removeEventListener('scroll', handleScroll);
		};
	}, []);

	const onLastDesignReached = () => {
		if (!scrollRef.current) return;
		let { totalElements, data } = scrollRef.current;
		if (totalElements > 0 && totalElements !== data.length) {
			setPage((prev) => prev + 1);
		}
	};

	const handleScroll = () => {
		if (isPageReachBottom()) {
			onLastDesignReached();
		}
	};

	const onClickDesignImage = (designId) => {
		setDesignIdToView(designId);
	};

	const onCloseViewModal = () => {
		setDesignIdToView(null);
		dispatch(clearDesignViewModalInfo({}));
	};

	const onLikeProduct = (id) => {
		dispatch(likeProduct(id));
	};

	const isCollectionSelected = () => selectedSortingOption?.code === 'COLLECTION';

	const handleAddToDraft = async (designIds) => {
		try {
			dispatch(openLoader());
			await addToDraft(designIds, folderId);
			dispatch(fetchFavouriteProducts(page, 30, '', selectedSortingOption?.code, folderId));
			fetchDraftedProducts();
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}
	};

	const getDisplayName = (item, index) => {
		if (isCollectionSelected()) {
			return groupedData[item][0]?.productResponse?.collectionName;
		} else {
			return Object.keys(groupedData)[index];
		}
	};

	const getCollectionDisplayName = (item, index) => {
		return isCollectionSelected()
			? groupedData[item][0]?.productResponse?.collectionName
			: Object.keys(groupedData)[index];
	};

	const renderDesignList = (item) => {
		if (item?.length > 0) {
			return item.map((design, index) => (
				<DesignItem
					design={{
						...design?.productResponse,
						addedToFavouriteDateTime: design?.addedToFavouriteDateTime,
						liked: true,
						isLinked: design?.isLinked,
						addedToFavouriteBy: design?.addedToFavouriteBy
					}}
					isSet={design?.productResponse?.isSet}
					isPack={design?.productResponse?.isPack}
					index={index}
					selectedProducts={selectedProducts}
					setSelectedProducts={setSelectedProducts}
					setSelectedProductsWithDetails={setSelectedProductsWithDetails}
					selectedProductsWithDetails={selectedProductsWithDetails}
					onClickDesignImage={openDesignViewModal}
					key={`design_${index}`}
					onLikeProduct={onLikeProduct}
					fromPage={'FAVOURITE_PAGE'}
					onAddtoDraft={handleAddToDraft}
					productWiseTags={productWiseTags}
					fetchAllTags={fetchAllTags}
					updateProductTagOnSelect={updateProductTagOnSelect}
					showFabricStatus={productIdsWithoutBodyFabric?.includes(design?.productResponse?.id)}
					onEditSet={handleEditSet}
					setIsOpenDeleteModal={setIsOpenDeleteConfirmation}
					setSelectedSetProduct={setSelectedSetProduct}
					setRequestType={setRequestType}
					onDeleteSet={handleDeleteSet}
				/>
			));
		}
	};

	const renderGroupData = () => {
		return Object.keys(groupedData).map((item, index) => (
			<div className="favorite-collection-container p-0 bg-white" key={`fav_collection_${index}`}>
				<span
					onClick={() =>
						isCollectionSelected() &&
						window.open(
							`/collections/view/${groupedData[item][0]?.productResponse?.collectionId}`,
							'_blank'
						)
					}
					className="semibold-16 mr-2 cursor-pointer"
				>
					<span className="mr-2 collection-name">
						{getDisplayName(item, index)} {`(${groupedData[item]?.length})`}
					</span>
					{isCollectionSelected() && (
						<CommonImage alt="external-link" src="/icons/external-link.svg" />
					)}
				</span>

				<div className="design-list-container favourite-list-container mt-3">
					<div
						className={`design-list-row ${
							defaultViewType === LIST_VIEW_TYPE.NARROW_VIEW
								? 'narrow-view'
								: defaultViewType === LIST_VIEW_TYPE.IMAGE_VIEW
									? 'image-view'
									: ''
						}`}
					>
						{renderDesignList(groupedData[item])}
					</div>
				</div>
			</div>
		));
	};

	const renderListViewGroupData = () => {
		return Object.keys(groupedData).map((item, index) => (
			<div key={`fav_collection_${index}`} className="sample-list-details-table mb-4">
				<div
					onClick={() =>
						isCollectionSelected() &&
						window.open(
							`/collections/view/${groupedData[item][0]?.productResponse?.collectionId}`,
							'_blank'
						)
					}
					className="semibold-16 mr-2 cursor-pointer pb-3"
				>
					<span className="mr-2 collection-name">
						{getCollectionDisplayName(item, index)} {`(${groupedData[item]?.length})`}
					</span>
					{isCollectionSelected() && (
						<CommonImage alt="external-link" src="/icons/external-link.svg" />
					)}
				</div>
				<FavouriteProductsListView
					productList={groupedData[item]}
					onClickDesignImage={onClickDesignImage}
					selectedProducts={selectedProducts}
					setSelectedProducts={setSelectedProducts}
					index={index}
					productWiseTags={productWiseTags}
					fetchAllTags={fetchAllTags}
					updateProductTagOnSelect={updateProductTagOnSelect}
					setSelectedProductsWithDetails={setSelectedProductsWithDetails}
					selectedProductsWithDetails={selectedProductsWithDetails}
				/>
			</div>
		));
	};

	const onReset = () => {
		setIsShowAddCollectionPopup(false);
		setSelectedProducts([]);
		setSelectedProductsWithDetails([]);
	};

	const handleSelectAll = () => {
		const designIds = designList?.data?.map((product) => product?.productResponse?.id);
		setSelectedProducts((prevSelected) => [...new Set([...prevSelected, ...designIds])]);
		setSelectedProductsWithDetails(designList?.data?.map((product) => product?.productResponse));
	};

	const getProductWithNoBodyFabric = () => {
		const listOfProduct = [];
		designList.data.forEach(({ productResponse }) => {
			if (selectedProducts.includes(productResponse?.id)) {
				if (productResponse?.isSet) {
					return productResponse?.setMemberProducts.forEach((member) => {
						if (!member.fabricDetails && !member.fabricName) {
							listOfProduct.push(productResponse);
						}
					});
				} else {
					if (!productResponse.fabricDetails && !productResponse.fabricName) {
						listOfProduct.push(productResponse);
					}
				}
			}
		});
		return listOfProduct;
	};

	const onPlaceOrder = () => {
		const productWithoutBodyFabric = getProductWithNoBodyFabric();
		if (productWithoutBodyFabric.length > 0) {
			dispatch(openLoader());
			let productIds = productWithoutBodyFabric.map((item) => item.id);
			setProductIdsWithoutBodyFabric(productIds);
			toastError('Please add body fabric for place order.');
			dispatch(closeLoader());
		} else {
			dispatch(storeProductData('selectedProductIds', selectedProducts));
			setProductIdsWithoutBodyFabric([]);

			let soldIds = designList?.data
				.filter((item) => item.productResponse.availabilityStatus === 'SOLD')
				?.map((item) => item.productResponse.id);

			let unsoldIds = designList?.data
				.filter((item) => item.productResponse.availabilityStatus !== 'SOLD')
				?.map((item) => item.productResponse.id);

			let isSelectedSoldIdExist = selectedProducts.some((id) => soldIds.includes(id));
			let isSelectedUnsoldIdExist = selectedProducts.some((id) => unsoldIds.includes(id));

			if (isSelectedSoldIdExist && isSelectedUnsoldIdExist) {
				toastError("Can't place order for sold and unsold styles together.");
			} else if (isSelectedSoldIdExist || isSelectedUnsoldIdExist) {
				setIsOpenPlaceOrder(true);
			}
		}
	};

	const createPack = async () => {
		try {
			dispatch(openLoader());
			let params = `?entityId=${folderId}&entityType=FAVOURITE_FOLDER&name=${encodeURIComponent(selectedProductsWithDetails[0].referenceNumber)}&productId=${
				selectedProductsWithDetails[0].id
			}`;

			const data = await createSetOrder('create-pack', params);
			if (data) {
				fetchFavProducts();
				handleRestState();
			}
			dispatch(closeLoader());
		} catch (error) {
			printApiError(error);
			dispatch(closeLoader());
		}
	};

	const handleRestState = () => {
		setProductIdsWithoutBodyFabric([]);
		setSelectedProducts([]);
		setSelectedProductsWithDetails([]);
		setIsUnSelectAll(false);
		setIsSelectOnNextPage(false);
		setIsShowInventoryModal(false);
		setSetOrderType('');
		setRequestType('CREATE');
	};

	const onActionClick = (type) => {
		switch (type) {
			case 'COLLECTION':
				setIsShowAddCollectionPopup(true);
				break;
			case 'DOWNLOAD_REPORT':
				downloadReportByProductIds(selectedProducts);
				break;
			case 'PLACE_ORDER':
				onPlaceOrder();
				break;
			case 'SAMPLE_LOCATION_DETAILS':
				setIsShowLocationModal(true);
				break;
			case 'FETCH_NEXT_PAGE':
				setPage(page + 1);
				setIsSelectOnNextPage(true);
				break;
			case 'UPDATE_SAMPLE_LOCATION':
				setIsShowUpdateLocationModal(true);
				break;
			case 'MOVE_TO_DRAFT':
				const linkedProductIds = excludeLinkedProducts();
				const validProductIds = selectedProducts.filter((id) => !linkedProductIds?.includes(id));

				if (validProductIds.length !== selectedProducts.length) {
					toastWarning('Some selected products are linked and cannot be drafted');
					return;
				}

				if (validProductIds.length) {
					handleAddToDraft(validProductIds.join(','));
				}
				break;
			case 'INVENTORY_STICKER':
				setIsShowInventoryModal(true);
				break;
			case 'SELECT_ALL':
				setIsUnSelectAll(true);
				handleSelectAll();
				break;
			case 'ADD_FOLDER':
				setIsShowSelectFolderPopup(true);
				break;
			case 'PRESENTATION':
				setIsPlayPresentation(true);
				break;
			case 'ADD_TAG':
				setIsShowTags(true);
				break;
			case 'CREATE_SET':
				setIsShowSetModal(true);
				break;
			case 'CREATE_PACK':
				createPack();
				break;

			case 'CLEAR':
				handleRestState();
				break;
			default:
				break;
		}
	};

	const downloadReportByProductIds = (productIds = []) => {
		dispatch(openLoader());
		let params = `?productIds=${productIds.join(',')}&inventoryCountryIds=${selectedLocationsToDownload?.join(
			','
		)}`;
		ApiClient.GET_FILE_DATA('downloadUserWiseReport', params)
			.then((response) => {
				downloadBlobFIle(response);
				dispatch(closeLoader());
				setIsShowLocationModal(false);
				setSelectedLocationsToDownload([]);
			})
			.catch(() => {
				toastError('Something went wrong!');
				dispatch(closeLoader());
			});
	};

	useEffect(() => {
		if (designIdToView !== null) {
			openDesignViewModal();
		}
	}, [designIdToView]);

	const isRegularSort = () => {
		return (
			selectedSortingOption?.code === 'NEWEST' ||
			selectedSortingOption?.code === 'OLDEST' ||
			selectedSortingOption?.code === 'STYLE_NO'
		);
	};

	const openDesignViewModal = (item) => {
		dispatch(
			setDesignViewModalInfo({
				mergeProductList: item?.setMemberProductIds || [],
				isShowMergeTechPack: item?.isSet,
				id: item?.id,
				onCloseViewModal: onCloseViewModal,
				updateProductStatus: () =>
					dispatch(
						fetchFavouriteProducts(page, 30, '', selectedSortingOption?.code, folderId)
					).finally(() => {
						setIsLoading(false);
					}),
				source: 'PRODUCT_LIST',
				designIdList: designIdList,
				loadMoreDesign: true,
				onLastDesignReached: onLastDesignReached
			})
		);
	};

	const isHideSetOrder = () => {
		return false;
	};

	const handleCopyToFolder = (selectedFolder, handleSuccess) => {
		dispatch(
			likeProduct(
				selectedProducts.join(','),
				() => handleSuccess(selectedFolder),
				'',
				selectedFolder?.id
			)
		);
	};

	const getRemainingStyles = () => {
		const totalElements = designList?.totalElements || 0;
		const selectedCount = selectedProducts?.length || 0;
		const remaining = totalElements - selectedCount;

		return remaining < 30 ? remaining : 30;
	};

	const updateProductTagOnSelect = (payload) => {
		const updatedProducts = designList?.data?.map((product) => {
			const matchingPayload = payload.find((item) => item.id === product?.productResponse?.id);
			if (matchingPayload) {
				return {
					...product,
					productResponse: {
						...product.productResponse,
						tagResponseList: matchingPayload.tagResponseList
					}
				};
			}
			return product;
		});

		setDesignList((prev) => ({
			...prev,
			data: updatedProducts
		}));
	};

	const isShowRepeatOrderTag = () => {
		let soldIds = designList?.data
			.filter((item) => item.productResponse.availabilityStatus === 'SOLD')
			?.map((item) => item.productResponse.id);
		return selectedProducts.some((id) => soldIds.includes(id));
	};

	const updateProductCards = () => {
		fetchFavProducts();
		handleRestState();
	};

	const handleEditSet = (type, item) => {
		setSetOrderType(type);
		setSelectedSetProduct(item);
		setIsShowSetModal(true);
	};

	const handleDeleteSet = (type, item) => {
		setSetOrderType(type);
		setSelectedSetProduct(item);
		setIsOpenDeleteConfirmation(true);
	};

	const isShowSetPackOption = () => {
		const hasSetOrPack = selectedProductsWithDetails.some((item) => item?.isSet || item?.isPack);

		if (hasSetOrPack) {
			return {};
		}

		const eligibleProducts = selectedProductsWithDetails.filter((item) => !item?.isSet && !item?.isPack);

		if (eligibleProducts?.length === 1) {
			return {
				id: 'CREATE_PACK',
				title: 'Create Pack',
				value: 'CREATE_PACK'
			};
		}

		if (eligibleProducts?.length > 1) {
			return {
				id: 'CREATE_SET',
				title: 'Create Set',
				value: 'CREATE_SET'
			};
		}

		return {};
	};

	const getSelectedProductIdsWithSetMember = () => {
		const ids = [];

		selectedProductsWithDetails.forEach((item) => {
			if (item?.isSet) {
				item?.setMemberProductIds.forEach((member) => {
					ids.push(member);
				});
			} else {
				ids.push(item.id);
			}
		});

		return ids;
	};

	return allFavourites?.data?.length === 0 ? (
		<div className="no-favorite">
			<NoData message={'No favourite added yet'} />
		</div>
	) : (
		<>
			{defaultViewType === LIST_VIEW_TYPE.LIST_VIEW ? (
				<div className="favorite-collection-wrapper">
					<div className="favorite-collection-container p-0 bg-white">
						{isRegularSort() ? (
							<FavouriteProductsListView
								productList={designList?.data}
								onClickDesignImage={openDesignViewModal}
								selectedProducts={selectedProducts}
								setSelectedProducts={setSelectedProducts}
								productWiseTags={productWiseTags}
								fetchAllTags={fetchAllTags}
								updateProductTagOnSelect={updateProductTagOnSelect}
								setSelectedProductsWithDetails={setSelectedProductsWithDetails}
								selectedProductsWithDetails={selectedProductsWithDetails}
							/>
						) : (
							renderListViewGroupData()
						)}
					</div>
				</div>
			) : (
				<div className="favorite-collection-wrapper">
					{isRegularSort() ? (
						<div className="favorite-collection-container p-0 bg-white">
							<div className="design-list-container favourite-list-container mt-3">
								<div
									className={`design-list-row ${
										defaultViewType === LIST_VIEW_TYPE.NARROW_VIEW
											? 'narrow-view'
											: defaultViewType === LIST_VIEW_TYPE.IMAGE_VIEW
												? 'image-view'
												: ''
									}`}
								>
									{renderDesignList(designList?.data)}
								</div>
							</div>
						</div>
					) : (
						renderGroupData()
					)}
				</div>
			)}
			{selectedProducts?.length > 0 && (
				<BottomPopup
					length={`${selectedProducts?.length}/${designList?.totalElements}`}
					type={'FAVOURITE_PAGE'}
					showUnSelectAll={isUnSelectAll}
					onActionClick={(type) => onActionClick(type)}
					isHideSetOrder={isHideSetOrder}
					isDisableFetchNextPage={getRemainingStyles() === 0}
					remainingStyles={getRemainingStyles()}
					setPackOption={isShowSetPackOption()}
				/>
			)}
			{isShowUpdateLocationModal && (
				<UpdateSampleLocation
					setIsShowUpdateLocationModal={setIsShowUpdateLocationModal}
					selectedProductIds={getSelectedProductIdsWithSetMember()}
					isOnTheWay
				/>
			)}
			<div className={`${isShowSelectFolderPopup ? 'show' : 'hide'}`}>
				<SelectFolderPopup
					title="Add to Folder"
					isShowCreateNew={isCreateNewFolder}
					setIsShowCreateNew={setIsCreateNewFolder}
					onClose={() => setIsShowSelectFolderPopup(false)}
					handleCopyToFolder={handleCopyToFolder}
				/>
			</div>
			{isShowLocationModal && (
				<SampleLocationDetailsModal
					onClose={() => {
						setSelectedLocationsToDownload([]);
						setIsShowLocationModal(false);
					}}
					downloadReportByProductIds={downloadReportByProductIds}
					selectedLocationsToDownload={selectedLocationsToDownload}
					setSelectedLocationsToDownload={setSelectedLocationsToDownload}
					selectedProductIds={getSelectedProductIdsWithSetMember(selectedProductsWithDetails)}
				/>
			)}
			{isShowInventoryModal && (
				<InventoryAndStickerModal
					onClose={() => setIsShowInventoryModal(false)}
					isDesignEdit={true}
					productIds={getSelectedProductIdsWithSetMember()}
					designList={designList?.data}
					setIsShowInventoryModal={setIsShowInventoryModal}
					onSuccess={() =>
						dispatch(fetchFavouriteProducts(page, 20, '', selectedSortingOption?.code, folderId))
					}
				/>
			)}
			<CollectionPresentation
				isPlay={isPlayPresentation}
				collectionName={folderInfo?.name}
				collectionId={folderId}
				onCLoseModal={() => setIsPlayPresentation(false)}
				entityType="FAVOURITE_FOLDER"
				selectedProductIds={selectedProducts}
			/>
			{startLoader && <TimeBasedLoader startLoader={startLoader} />}
			{isShowAddCollectionPopup && (
				<AddDesignToCollection
					type="ADD_NEW"
					title="Copy to collection"
					onAddDesign={onReset}
					onCancel={onReset}
					isMoveActive={false}
					designIdList={selectedProducts}
				/>
			)}
			{isShowTags && (
				<TagUpdateModal
					type="ADD_NEW"
					title="Tag"
					onCancel={() => {
						setIsShowTags(false);
					}}
					allTagList={productWiseTags}
					commonTags={commonTags}
					updateTagList={updateProductTagOnSelect}
					fetchAllTags={fetchAllTags}
					favouriteProductIdList={selectedProducts}
					isFavouriteProduct={true}
				/>
			)}
			{isOpenPlaceOrder && (
				<CreateOrderModal
					isShowRepeatOrderTag={isShowRepeatOrderTag()}
					onClose={async () => {
						setIsOpenPlaceOrder(false);
						onActionClick('CLEAR');
						dispatch(storeProductData('selectedProductIds', []));
					}}
					brandId={brandId}
					folderId={folderId}
				/>
			)}
			{isShowSetModal && (
				<CreateSetOrder
					requestType={requestType}
					setOrderType="SET"
					onClose={() => {
						setIsShowSetModal(false);
						setRequestType('CREATE');
					}}
					productIds={selectedProducts}
					updateProductCard={updateProductCards}
					selectedProduct={selectedSetProduct}
					entityId={folderId}
					entityType={'FAVOURITE_FOLDER'}
				/>
			)}

			{isOpenDeleteConfirmation && (
				<DeleteSetOrder
					selectedProduct={selectedSetProduct}
					setOrderType={setOrderType}
					onClose={() => {
						setSelectedSetProduct(null);
						setIsOpenDeleteConfirmation(false);
						setSetOrderType('');
					}}
					updateProductCard={updateProductCards}
				/>
			)}
		</>
	);
};

export default ProductFavourite;
